/**
 * Onboarding copy overrides (Skip request 2026-07-07; migration 265).
 *
 * Sysadmin-editable text for the acclimation surfaces (welcome screen,
 * Getting-started rows, promotion notice). Keys are namespace-qualified
 * i18n keys ('pages:welcomeScreen.body'). Resolution follows the CLAUDE.md
 * three-tier admin-taxonomy pattern:
 *   1. DB override — translations[lang], else value (English override)
 *   2. Static locale JSON — the normal t() result
 *   3. English static (i18next's own fallback)
 * Overrides support {{var}} interpolation with the same vars t() receives.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from './client';
import { fetchForKey, invalidateAndRefetch, CACHE_UPDATE_EVENT } from './queryCache';

const CACHE_KEY = 'onboardingCopy';

export interface OnboardingCopyEntry {
  copyKey: string;
  value: string;
  translations: string; // JSON: {"fr": "...", ...}
  updatedAt: string;
}

export type OnboardingCopyMap = Map<string, { value: string; translations: Record<string, string> }>;

export function fetchOnboardingCopy(): Promise<OnboardingCopyEntry[]> {
  return apiFetch<OnboardingCopyEntry[]>('/api/onboarding/copy');
}

/** Sysadmin upsert; empty value clears the override. */
export async function saveOnboardingCopy(
  key: string,
  value: string,
  translations?: Record<string, string>,
): Promise<void> {
  await apiFetch(`/api/onboarding/copy/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({
      value,
      translations: translations ? JSON.stringify(translations) : undefined,
    }),
  });
  invalidateAndRefetch(CACHE_KEY);
}

function toMap(entries: OnboardingCopyEntry[]): OnboardingCopyMap {
  const map: OnboardingCopyMap = new Map();
  for (const e of entries || []) {
    let tr: Record<string, string> = {};
    try {
      tr = JSON.parse(e.translations || '{}');
    } catch {
      /* malformed translations → English override only */
    }
    map.set(e.copyKey, { value: e.value, translations: tr });
  }
  return map;
}

/**
 * Resolve an override for a namespace-qualified key in the given language.
 * Returns null when no override exists (caller falls through to t()).
 */
export function resolveCopyOverride(
  map: OnboardingCopyMap | null | undefined,
  lang: string,
  qualifiedKey: string,
  vars?: Record<string, any>,
): string | null {
  const entry = map?.get(qualifiedKey);
  if (!entry) return null;
  const short = String(lang || 'en').split('-')[0];
  const raw = (short !== 'en' && entry.translations[short]) || entry.value;
  if (!raw) return null;
  if (!vars) return raw;
  return raw.replace(/\{\{(\w+)\}\}/g, (_m, name: string) =>
    vars[name] != null ? String(vars[name]) : '',
  );
}

export interface UseOnboardingCopyResult {
  copyMap: OnboardingCopyMap;
  loading: boolean;
  refetch: () => void;
}

/** Cached hook — one fetch app-wide; invalidated by saveOnboardingCopy. */
export function useOnboardingCopy(enabled: boolean = true): UseOnboardingCopyResult {
  const mountedRef = useRef(true);
  const [copyMap, setCopyMap] = useState<OnboardingCopyMap>(new Map());
  const [loading, setLoading] = useState(enabled);

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !enabled) return;
    try {
      const rows = await fetchForKey(CACHE_KEY, async () => fetchOnboardingCopy(), {
        force: true,
      });
      if (!mountedRef.current) return;
      setCopyMap(toMap(rows || []));
      setLoading(false);
    } catch {
      if (!mountedRef.current) return;
      setLoading(false); // no overrides → static i18n everywhere
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) refetch();
    else setLoading(false);
    return () => {
      mountedRef.current = false;
    };
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === CACHE_KEY) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [enabled, refetch]);

  return { copyMap, loading, refetch };
}
